import { useCallback } from 'react';

import { createQuestionGroup, deleteQuestionGroup, updateQuestionGroup } from '@api/instant_survey';
import { toast } from '@utilities';

import { DEFAULT_QUESTION_GROUP_NAME } from './constants';
import { createQuestionKey, isQuestionGroupNameTaken } from './helpers';

/**
 * lastGroupMemberIndex
 * Index of the last question in `questions` that belongs to `groupId` or any of its child groups
 * (-1 if none). Used to keep a group's questions — and its nested groups' questions — contiguous
 * when inserting into the flat question list.
 */
const lastGroupMemberIndex = (questions, groupId, groups) => {
  if (groupId == null) return -1;
  const groupIds = new Set([groupId]);
  Object.values(groups).forEach((group) => {
    if (group.parentGroup === groupId) groupIds.add(group.id);
  });
  return questions.reduce(
    (lastIndex, question, index) => (groupIds.has(question.questionGroup) ? index : lastIndex),
    -1
  );
};

/**
 * useQuestionGroupHelpers
 * Question group create/rename/randomize/assign/nest callbacks. Read survey state via surveyRef so
 * callbacks don't need survey in deps.
 * @param {Object} params
 * @param {Object} params.surveyRef ref whose `.current` holds the live survey
 * @param {Function} params.updateSurvey survey state updater
 * @return {{
 *   assignGroupToParent: Function,
 *   assignQuestionToGroup: Function,
 *   createGroupForQuestion: Function,
 *   renameGroup: Function,
 *   toggleGroupRandomize: Function,
 *   toggleQuestionAnchored: Function,
 * }}
 */
export const useQuestionGroupHelpers = ({ surveyRef, updateSurvey }) => {
  const createGroupForQuestion = useCallback(
    async (question) => {
      const targetKey = createQuestionKey(question);

      // Groups are created without a name prompt, so the name has to be unique by construction.
      let name = DEFAULT_QUESTION_GROUP_NAME;
      let nameSuffix = 2;
      while (isQuestionGroupNameTaken({ name, questionGroups: surveyRef.current.questionGroups })) {
        name = `${DEFAULT_QUESTION_GROUP_NAME} ${nameSuffix}`;
        nameSuffix += 1;
      }

      const group = await createQuestionGroup({ name });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(group.id)]: group,
          },
          questions: surveyRef.current.questions.map((q) =>
            createQuestionKey(q) === targetKey ? { ...q, questionGroup: group.id } : q
          ),
        },
        { markDirty: true }
      );
    },
    [surveyRef, updateSurvey]
  );

  const renameGroup = useCallback(
    async (groupId, name) => {
      await updateQuestionGroup({ id: groupId, name });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(groupId)]: {
              ...(surveyRef.current.questionGroups?.[String(groupId)] || { id: groupId }),
              name,
            },
          },
        },
        { markDirty: false }
      );
    },
    [surveyRef, updateSurvey]
  );

  const toggleGroupRandomize = useCallback(
    async (groupId) => {
      const existing = surveyRef.current.questionGroups?.[String(groupId)];
      const nextRandomize = !(existing?.randomizeOrder ?? false);
      await updateQuestionGroup({ id: groupId, randomizeOrder: nextRandomize });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(groupId)]: {
              ...(existing || { id: groupId }),
              randomizeOrder: nextRandomize,
            },
          },
        },
        { markDirty: false }
      );
    },
    [surveyRef, updateSurvey]
  );

  // Nest a group under a parent (parentGroupId) or detach it (null). When nesting, the group's
  // questions are repositioned directly after the parent group's run so the flat order stays
  // contiguous and buildRenderList can derive the nested tree.
  const assignGroupToParent = useCallback(
    async (groupId, parentGroupId) => {
      const oldParentGroupId =
        surveyRef.current.questionGroups?.[String(groupId)]?.parentGroup ?? null;

      const updatedGroup = await updateQuestionGroup({
        id: groupId,
        parentGroup: parentGroupId,
      });
      const latestGroups = surveyRef.current.questionGroups || {};
      const latestQuestions = surveyRef.current.questions;
      const movingQuestions = latestQuestions.filter(
        ({ questionGroup }) => questionGroup === groupId
      );
      let updatedQuestions = latestQuestions;

      if (parentGroupId != null && movingQuestions.length > 0) {
        const withoutMovingQuestions = latestQuestions.filter(
          ({ questionGroup }) => questionGroup !== groupId
        );
        const lastParentQuestionIndex = lastGroupMemberIndex(
          withoutMovingQuestions,
          parentGroupId,
          latestGroups
        );
        const insertAt =
          lastParentQuestionIndex === -1
            ? withoutMovingQuestions.length
            : lastParentQuestionIndex + 1;
        updatedQuestions = [...withoutMovingQuestions];
        updatedQuestions.splice(insertAt, 0, ...movingQuestions);
      }

      const nextGroups = {
        ...latestGroups,
        [String(groupId)]: updatedGroup,
      };
      const orphanedParentId =
        oldParentGroupId != null &&
        oldParentGroupId !== parentGroupId &&
        latestGroups[String(oldParentGroupId)] != null &&
        !latestQuestions.some(({ questionGroup }) => questionGroup === oldParentGroupId) &&
        !Object.values(nextGroups).some(({ parentGroup }) => parentGroup === oldParentGroupId)
          ? oldParentGroupId
          : null;
      const orphanedParentGroup =
        orphanedParentId != null ? latestGroups[String(orphanedParentId)] : null;

      if (orphanedParentId != null) {
        delete nextGroups[String(orphanedParentId)];
      }

      updateSurvey(
        {
          questionGroups: nextGroups,
          questions: updatedQuestions,
        },
        { markDirty: updatedQuestions !== latestQuestions }
      );

      if (orphanedParentId == null) return;

      try {
        await deleteQuestionGroup({ id: orphanedParentId });
      } catch {
        updateSurvey(
          {
            questionGroups: {
              ...(surveyRef.current.questionGroups || {}),
              [String(orphanedParentId)]: orphanedParentGroup,
            },
          },
          { markDirty: false }
        );
        toast.error('Unable to delete an empty question group. Please refresh and try again.');
      }
    },
    [surveyRef, updateSurvey]
  );

  const toggleQuestionAnchored = useCallback(
    (question) => {
      const targetQuestionKey = createQuestionKey(question);
      updateSurvey(
        {
          questions: surveyRef.current.questions.map((currentQuestion) =>
            createQuestionKey(currentQuestion) === targetQuestionKey
              ? { ...currentQuestion, anchored: !currentQuestion.anchored }
              : currentQuestion
          ),
        },
        { markDirty: true }
      );
    },
    [surveyRef, updateSurvey]
  );

  // Move a question into a group (groupId) or out of its group (null), repositioning it so group
  // boundaries stay contiguous:
  //  - into a group  → inserted as the group's LAST member;
  //  - out of a group → moved to directly below its old group's run (the question is kept).
  // Groups left without members are auto-deleted (parent groups are retained while a child remains).
  const assignQuestionToGroup = useCallback(
    async (question, groupId) => {
      const targetKey = createQuestionKey(question);
      const questions = surveyRef.current.questions;
      const moving = questions.find((q) => createQuestionKey(q) === targetKey);
      if (!moving) return;
      const oldGroupId = moving.questionGroup ?? null;
      const originalIndex = questions.findIndex((q) => createQuestionKey(q) === targetKey);

      const without = questions.filter((q) => createQuestionKey(q) !== targetKey);
      const currentGroups = surveyRef.current.questionGroups || {};

      let insertAt;
      if (groupId != null) {
        const lastIdx = lastGroupMemberIndex(without, groupId, currentGroups);
        insertAt = lastIdx === -1 ? without.length : lastIdx + 1;
      } else {
        // Ungrouping: drop directly below the old group's run; if that group is now empty (or the
        // question had no group), keep it where it was.
        const lastIdx = lastGroupMemberIndex(without, oldGroupId, currentGroups);
        insertAt = lastIdx === -1 ? Math.min(originalIndex, without.length) : lastIdx + 1;
      }

      const updatedQuestions = [...without];
      updatedQuestions.splice(insertAt, 0, {
        ...moving,
        anchored: groupId == null ? false : moving.anchored,
        questionGroup: groupId,
      });

      // auto-remove groups that have become empty after this assignment
      const remainingGroupIds = new Set(
        updatedQuestions.filter((q) => q.questionGroup != null).map((q) => String(q.questionGroup))
      );

      const nextGroups = { ...currentGroups };
      const retainedGroupIds = new Set(remainingGroupIds);
      remainingGroupIds.forEach((id) => {
        const parentGroupId = currentGroups[id]?.parentGroup;
        if (parentGroupId != null) retainedGroupIds.add(String(parentGroupId));
      });
      const orphanIds = Object.keys(currentGroups).filter((id) => !retainedGroupIds.has(id));

      orphanIds.forEach((id) => delete nextGroups[id]);

      updateSurvey(
        { questionGroups: nextGroups, questions: updatedQuestions },
        { markDirty: true }
      );

      if (orphanIds.length === 0) return;

      // Delete emptied groups on the backend. If a DELETE fails, restore that group in local state so
      // the client matches the backend (the group still exists) instead of silently orphaning it.
      const results = await Promise.allSettled(
        orphanIds.map((id) => deleteQuestionGroup({ id: Number(id) }))
      );
      const failedGroupIds = orphanIds.filter((id, index) => results[index].status === 'rejected');

      if (failedGroupIds.length > 0) {
        updateSurvey(
          {
            questionGroups: {
              ...(surveyRef.current.questionGroups || {}),
              ...Object.fromEntries(failedGroupIds.map((id) => [id, currentGroups[id]])),
            },
          },
          { markDirty: false }
        );
        toast.error('Unable to delete an empty question group. Please refresh and try again.');
      }
    },
    [surveyRef, updateSurvey]
  );

  return {
    assignGroupToParent,
    assignQuestionToGroup,
    createGroupForQuestion,
    renameGroup,
    toggleGroupRandomize,
    toggleQuestionAnchored,
  };
};
